import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const steps = [
  { icon: 'ri-phone-line', title: 'Emergency Call', desc: 'Call us 24/7 at (209) 451-2288. We mobilize immediately after any storm event in Merced County.' },
  { icon: 'ri-search-eye-line', title: 'Damage Assessment', desc: 'We inspect roof damage, flooding, fallen trees, and structural issues to create a full restoration plan.' },
  { icon: 'ri-shield-line', title: 'Emergency Tarping & Board-Up', desc: 'We secure your property immediately to prevent further water intrusion and weather damage.' },
  { icon: 'ri-drop-line', title: 'Water Extraction & Drying', desc: 'Standing water is removed and industrial drying equipment is deployed to dry out your structure.' },
  { icon: 'ri-delete-bin-line', title: 'Debris Removal', desc: 'Storm debris, fallen trees, and damaged materials are safely cleared from your property.' },
  { icon: 'ri-home-smile-line', title: 'Full Restoration', desc: 'We repair roofs, siding, windows, and interiors to restore your property to pre-storm condition.' },
];

const reasons = [
  { icon: 'ri-time-line', title: 'Rapid Storm Response', desc: 'We mobilize crews immediately after storms to prevent secondary damage from rain and wind.' },
  { icon: 'ri-award-line', title: 'IICRC Certified', desc: 'Certified professionals trained in storm and flood damage restoration techniques.' },
  { icon: 'ri-file-list-3-line', title: 'Insurance Claims Help', desc: 'We document all storm damage thoroughly and work directly with your insurance adjuster.' },
  { icon: 'ri-tools-line', title: 'Full-Service Repairs', desc: 'From roof tarping to complete reconstruction — we handle every aspect of storm recovery.' },
  { icon: 'ri-map-pin-line', title: 'Central Valley Experts', desc: 'We understand the unique storm patterns and weather risks of Merced and the Central Valley.' },
  { icon: 'ri-thumb-up-line', title: 'Trusted by Locals', desc: 'Hundreds of Merced homeowners and businesses have relied on us after major storm events.' },
];

export default function StormDamagePage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mercedwaterrestoration.com';

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Storm Damage Restoration",
      "name": "Storm Damage Restoration in Merced, CA",
      "description": "Emergency storm response including roof tarping, debris removal, water intrusion cleanup, and full property restoration after wind, rain, hail, and flood events in Merced County.",
      "url": `${siteUrl}/services/storm-damage`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "email": "info@mercedwaterrestoration.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Merced",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      },
      "availableChannel": {
        "@type": "ServiceChannel",
        "servicePhone": {
          "@type": "ContactPoint",
          "telephone": "+12094512288",
          "contactType": "Emergency Service",
          "availableLanguage": "English",
          "hoursAvailable": "24/7"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6dd7d18e32895917720e4204f51ef973.jpg"
            alt="Storm Damage Restoration Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/55 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-5xl mx-auto py-24">
          <span className="inline-block bg-sky-500/30 border border-sky-400/50 text-sky-200 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            24/7 Storm Emergency Response
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            Storm Damage Restoration<br />in Merced, CA
          </h1>
          <p className="text-xl text-white/85 mb-8 max-w-2xl mx-auto">
            Fast emergency response after wind, rain, hail, and flood events. We protect and restore your property around the clock.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>Call (209) 451-2288
            </a>
            <a href="/#contact" className="bg-white text-teal-700 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Get Free Estimate
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-5">Complete Storm Damage Cleanup & Repair</h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Severe storms can cause devastating damage in minutes — from roof punctures and flooding to fallen trees and broken windows. Acting fast is critical to preventing further water intrusion and structural deterioration.
              </p>
              <p className="text-gray-600 leading-relaxed mb-5">
                <strong>Indoor-Restore Services</strong> provides immediate storm damage response across Merced County and the Central Valley. Our crews are equipped to handle everything from emergency tarping to full property reconstruction.
              </p>
              <ul className="space-y-3">
                {[
                  'Wind & hail damage repair',
                  'Roof damage & emergency tarping',
                  'Flood & rainwater extraction',
                  'Fallen tree & debris removal',
                  'Window & door board-up',
                  'Structural drying & mold prevention',
                ].map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-700 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-500 text-lg"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-96">
              <img
                src="https://readdy.ai/api/search-image?query=restoration%20workers%20removing%20storm%20debris%20and%20damaged%20roofing%20materials%20from%20a%20residential%20property%20after%20a%20severe%20storm%20with%20professional%20equipment%20and%20safety%20gear%2C%20bright%20daylight%2C%20clean%20background&width=800&height=600&seq=sd-overview-02&orientation=landscape"
                alt="Storm debris removal and property restoration"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Storm Damage Restoration Process</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">From emergency securing to full rebuild — a proven process that gets your property back to normal fast.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="bg-white rounded-xl p-7 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <span className="text-3xl font-bold text-teal-100">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Indoor-Restore Services?</h2>
            <p className="text-white/80 max-w-2xl mx-auto">Merced's trusted storm damage restoration team — ready to respond when severe weather strikes.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {reasons.map((r, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-7 border border-white/20 hover:bg-white/20 transition-all">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-5">
                  <i className={`${r.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{r.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Storm Hit Your Property? We're Ready.</h2>
          <p className="text-gray-500 mb-8 text-lg">Call Indoor-Restore Services 24/7 for immediate storm damage response anywhere in Merced County and the Central Valley.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(209) 451-2288
            </a>
            <a href="mailto:info@mercedwaterrestoration.com" className="border-2 border-teal-600 text-teal-600 px-10 py-4 rounded-lg text-lg font-bold hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer">
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
